import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { usersAPI } from '../services/api';
import { X, Edit, Shield, ShieldOff, Lock, Unlock, Calendar, Package, DollarSign, Activity } from 'lucide-react';

const UserDetailModal = ({ isOpen, onClose, user, onEdit }) => {
  const [activeTab, setActiveTab] = useState('details');

  // Fetch user statistics
  const { data: statsData, isLoading: statsLoading } = useQuery(
    ['userStats', user?.id],
    () => usersAPI.getStats(user.id),
    {
      enabled: !!user?.id
    }
  );

  const stats = statsData?.stats || {};
  const recentActivity = statsData?.recentActivity || [];

  const getStatusBadge = (user) => {
    if (user.is_blocked) {
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
        <Lock className="w-3 h-3 mr-1" />
        Blocked
      </span>;
    }
    if (!user.is_active) {
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
        <ShieldOff className="w-3 h-3 mr-1" />
        Inactive
      </span>;
    }
    return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
      <Shield className="w-3 h-3 mr-1" />
      Active
    </span>;
  };

  const getRoleBadges = (userRoles) => {
    return userRoles.map(role => (
      <span key={role.id} className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-100 text-blue-800 mr-1">
        {role.display_name}
      </span>
    ));
  };

  const getPermissionBadges = (userRoles) => {
    const permissions = new Set();
    userRoles.forEach(role => {
      if (role.permissions) {
        Object.keys(role.permissions).forEach(permission => {
          if (role.permissions[permission]) {
            permissions.add(permission);
          }
        });
      }
    });

    return Array.from(permissions).map(permission => (
      <span key={permission} className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-green-100 text-green-800 mr-1">
        {permission.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
      </span>
    ));
  };

  if (!isOpen || !user) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">User Details</h2>
          <div className="flex items-center space-x-2">
            <button
              onClick={onEdit}
              className="flex items-center px-3 py-1 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Edit className="h-4 w-4 mr-1" />
              Edit
            </button>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="p-6">
          {/* User Header */}
          <div className="flex items-center space-x-4 mb-6">
            <div className="flex-shrink-0 h-16 w-16">
              <div className="h-16 w-16 rounded-full bg-blue-100 flex items-center justify-center">
                <span className="text-xl font-medium text-blue-600">
                  {user.full_name.split(' ').map(n => n[0]).join('').toUpperCase()}
                </span>
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-medium text-gray-900">{user.full_name}</h3>
              <p className="text-sm text-gray-500">{user.username} • {user.email}</p>
              <div className="flex items-center space-x-2 mt-2">
                {getStatusBadge(user)}
                <span className="text-sm text-gray-500">•</span>
                <span className="text-sm text-gray-500">
                  Member since {new Date(user.created_at).toLocaleDateString()}
                </span>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200 mb-6">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('details')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'details'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Details
              </button>
              <button
                onClick={() => setActiveTab('stats')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'stats'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Statistics
              </button>
              <button
                onClick={() => setActiveTab('activity')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'activity'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Recent Activity
              </button>
            </nav>
          </div>

          {/* Tab Content */}
          {activeTab === 'details' && (
            <div className="space-y-6">
              {/* Basic Information */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-sm font-medium text-gray-900 mb-3">Basic Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-500">Username</label>
                    <p className="text-sm text-gray-900">{user.username}</p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500">Email</label>
                    <p className="text-sm text-gray-900">{user.email}</p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500">Full Name</label>
                    <p className="text-sm text-gray-900">{user.full_name}</p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500">Status</label>
                    <div className="mt-1">{getStatusBadge(user)}</div>
                  </div>
                </div>
              </div>

              {/* Roles */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-sm font-medium text-gray-900 mb-3">Roles</h4>
                <div className="space-y-2">
                  <div className="flex flex-wrap gap-1">
                    {getRoleBadges(user.roles)}
                  </div>
                  {user.roles.length === 0 && (
                    <p className="text-sm text-gray-500">No roles assigned</p>
                  )}
                </div>
              </div>

              {/* Permissions */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-sm font-medium text-gray-900 mb-3">Permissions</h4>
                <div className="space-y-2">
                  <div className="flex flex-wrap gap-1">
                    {getPermissionBadges(user.roles)}
                  </div>
                  {getPermissionBadges(user.roles).length === 0 && (
                    <p className="text-sm text-gray-500">No specific permissions</p>
                  )}
                </div>
              </div>

              {/* Timestamps */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-sm font-medium text-gray-900 mb-3">Account Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-500">Created</label>
                    <p className="text-sm text-gray-900">{new Date(user.created_at).toLocaleString()}</p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500">Last Updated</label>
                    <p className="text-sm text-gray-900">{new Date(user.updated_at).toLocaleString()}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'stats' && (
            <div className="space-y-6">
              {statsLoading ? (
                <div className="flex items-center justify-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : (
                <>
                  {/* Statistics Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="bg-blue-50 rounded-lg p-4">
                      <div className="flex items-center">
                        <Package className="h-8 w-8 text-blue-600" />
                        <div className="ml-3">
                          <p className="text-sm font-medium text-blue-600">Total Shipments</p>
                          <p className="text-2xl font-bold text-blue-900">{stats.total_shipments || 0}</p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-green-50 rounded-lg p-4">
                      <div className="flex items-center">
                        <Package className="h-8 w-8 text-green-600" />
                        <div className="ml-3">
                          <p className="text-sm font-medium text-green-600">Delivered</p>
                          <p className="text-2xl font-bold text-green-900">{stats.delivered || 0}</p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-yellow-50 rounded-lg p-4">
                      <div className="flex items-center">
                        <Calendar className="h-8 w-8 text-yellow-600" />
                        <div className="ml-3">
                          <p className="text-sm font-medium text-yellow-600">Overdue</p>
                          <p className="text-2xl font-bold text-yellow-900">{stats.overdue || 0}</p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-4">
                      <div className="flex items-center">
                        <DollarSign className="h-8 w-8 text-purple-600" />
                        <div className="ml-3">
                          <p className="text-sm font-medium text-purple-600">Fully Paid</p>
                          <p className="text-2xl font-bold text-purple-900">{stats.fully_paid || 0}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Performance Metrics */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-gray-900 mb-3">Performance Metrics</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-500">Delivery Rate</label>
                        <p className="text-lg font-semibold text-gray-900">
                          {stats.total_shipments > 0 
                            ? `${Math.round((stats.delivered / stats.total_shipments) * 100)}%`
                            : '0%'
                          }
                        </p>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500">Payment Rate</label>
                        <p className="text-lg font-semibold text-gray-900">
                          {stats.total_shipments > 0 
                            ? `${Math.round((stats.fully_paid / stats.total_shipments) * 100)}%`
                            : '0%'
                          }
                        </p>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500">Overdue Rate</label>
                        <p className="text-lg font-semibold text-gray-900">
                          {stats.total_shipments > 0 
                            ? `${Math.round((stats.overdue / stats.total_shipments) * 100)}%`
                            : '0%'
                          }
                        </p>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {activeTab === 'activity' && (
            <div className="space-y-4">
              {statsLoading ? (
                <div className="flex items-center justify-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : recentActivity.length > 0 ? (
                <div className="space-y-3">
                  {recentActivity.map((activity, index) => (
                    <div key={index} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                      <div className="w-2 h-2 bg-blue-600 rounded-full mt-2"></div>
                      <div className="flex-1">
                        <p className="text-sm font-medium">
                          Status changed to: <span className="text-blue-600">{activity.to_status}</span>
                        </p>
                        <p className="text-xs text-gray-600">
                          Shipment #{activity.shipment_id} • {activity.customer_name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {new Date(activity.created_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Activity className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-500">No recent activity</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserDetailModal; 